import { create } from "zustand";
import type { CategoryPreferenceAttribute } from "@/api/models/api-types";

// Brand preference type
export interface BrandPreference {
  id: string;
  name: string;
}

// Size preferences type-based (key: type like "women_tops", "men_shoes")
export interface TypeSizePreferences {
  [type: string]: CategoryPreferenceAttribute[];
}

// Preferences store state
interface PreferencesStore {
  // Size Preferences - type-based size selections (women_tops, men_shoes, etc.)
  sizePreferences: TypeSizePreferences;

  // Favorite Brands - array of brand objects with id and name
  brandPreferences: BrandPreference[];

  // Actions - Size Preferences
  setTypeSizes: (type: string, sizes: CategoryPreferenceAttribute[]) => void;
  addTypeSize: (type: string, size: CategoryPreferenceAttribute) => void;
  removeTypeSize: (type: string, attributeValueId: string) => void;
  clearTypeSizes: (type: string) => void;

  // Actions - Brand Preferences
  setBrandPreferences: (brands: BrandPreference[]) => void;
  addBrandPreference: (brand: BrandPreference) => void;
  removeBrandPreference: (brandId: string) => void;
  clearBrandPreferences: () => void;

  // Reset
  reset: () => void;
}

const initialState = {
  sizePreferences: {} as TypeSizePreferences,
  brandPreferences: [] as BrandPreference[],
};

export const usePreferencesStore = create<PreferencesStore>()((set) => ({
  ...initialState,

  // Size Preferences Actions (Type-based)
  setTypeSizes: (type, sizes) => {
    set((state) => ({
      sizePreferences: {
        ...state.sizePreferences,
        [type]: sizes,
      },
    }));
  },

  addTypeSize: (type, size) => {
    set((state) => {
      const currentSizes = state.sizePreferences[type] || [];
      if (
        currentSizes.some((s) => s.attributeValueId === size.attributeValueId)
      )
        return state;
      return {
        sizePreferences: {
          ...state.sizePreferences,
          [type]: [...currentSizes, size],
        },
      };
    });
  },

  removeTypeSize: (type, attributeValueId) => {
    set((state) => {
      const currentSizes = state.sizePreferences[type] || [];
      return {
        sizePreferences: {
          ...state.sizePreferences,
          [type]: currentSizes.filter(
            (s) => s.attributeValueId !== attributeValueId
          ),
        },
      };
    });
  },

  clearTypeSizes: (type) => {
    set((state) => {
      const { [type]: _, ...rest } = state.sizePreferences;
      return { sizePreferences: rest };
    });
  },

  // Brand Preferences Actions
  setBrandPreferences: (brands) => {
    set({ brandPreferences: brands });
  },

  addBrandPreference: (brand) => {
    set((state) => {
      if (state.brandPreferences.some((b) => b.id === brand.id)) return state;
      return { brandPreferences: [...state.brandPreferences, brand] };
    });
  },

  removeBrandPreference: (brandId) => {
    set((state) => ({
      brandPreferences: state.brandPreferences.filter((b) => b.id !== brandId),
    }));
  },

  clearBrandPreferences: () => {
    set({ brandPreferences: [] });
  },

  // Reset
  reset: () => {
    set(initialState);
  },
}));

